import clsx from 'clsx'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useState } from 'react'

import { Icon } from '@wartek-id/icon'
import { Link } from '@wartek-id/link'
import { List, ListItem } from '@wartek-id/list'
import { Popover, PopoverContent, PopoverTrigger } from '@wartek-id/popover'
import { Text } from '@wartek-id/text'
import { Badge } from '@wartek-id/badge'

import styleHeader from 'app/KinerjaShared/components/PageHeader/PageHeader.module.css'
import { useAuth } from 'utils/auth'
import usePageHeader from 'app/KinerjaShared/components/PageHeader/usePageHeader'
import { PENGELOLAAN_KINERJA_BERANDA_PATH } from 'app/KinerjaShared/constants/path'
import { PeriodDialog } from 'app/KinerjaShared/components/PeriodDialog'

interface PemetaanKompetensiPageHeaderProps {
  title?: string
  icon?: string
  className?: string
  children?: React.ReactNode
}

export const PageHeader = ({
  title = 'Pengelolaan Kinerja Guru',
  className = '',
  children,
}: PemetaanKompetensiPageHeaderProps) => {
  const { session } = useAuth()
  const router = useRouter()

  const [isOpen, setIsOpen] = useState(false)

  const {
    isMenuOpen,
    setIsMenuOpen,
    isMultiplePeriodEnabled,
    isHavePeriod,
    formattedPeriod,
    periodNumber,
  } = usePageHeader()

  const isFirstPeriod = periodNumber === 1

  return (
    <>
      <div
        className={clsx(
          'fixed top-0',
          'w-full z-20',
          'bg-surface-default shadow-header',
          'flex justify-between items-center',
          className
        )}
      >
        <div className="flex items-center">
          <div className="flex-none pt-3 pb-2.5 px-6">
            <Link
              className="!p-0 !leading-[0] flex items-center gap-3 !no-underline !outline-none focus:!shadow-none"
              href={PENGELOLAAN_KINERJA_BERANDA_PATH}
            >
              <Image
                src="/images/kemendikbud2x.png"
                width={40}
                height={40}
                alt="Logo Tut Wuri Handayani"
                className="!select-none !pointer-events-none"
              />
              <Image
                src="/icon/guru-penggerak.svg"
                width={29}
                height={40}
                alt="Logo Merdeka Mengajar"
                className="!select-none !pointer-events-none"
              />
              <Text
                as="span"
                variant="heading-md"
                className="!pointer-events-none"
              >
                {title}
              </Text>
            </Link>
          </div>
          {isMultiplePeriodEnabled && isHavePeriod && (
            <div>
              <Badge
                className={clsx({
                  'gap-1': true,
                  '!bg-purple-20': isFirstPeriod,
                  '!bg-teal-20': !isFirstPeriod,
                })}
              >
                {isFirstPeriod ? (
                  <Icon className="!text-[16px] !w-4 !h-4">crop_square</Icon>
                ) : (
                  <Icon className="!text-[16px] !w-4 !h-4">change_history</Icon>
                )}
                {formattedPeriod}
              </Badge>
            </div>
          )}
        </div>
        <div className="flex-none flex px-6">
          <Popover
            onOpen={() => setIsMenuOpen(true)}
            onClose={() => setIsMenuOpen(false)}
          >
            <PopoverTrigger aria-label="Menu" className="flex gap-3 pt-6 pb-5">
              <Icon>group</Icon>
              <Text variant="action" className="shrink-0">
                {session?.user?.name}
              </Text>
              <Icon
                className={clsx(
                  'transition-transform transform',
                  isMenuOpen && 'rotate-180'
                )}
              >
                expand_more
              </Icon>
            </PopoverTrigger>

            <PopoverContent
              className={clsx(
                'transition-all mt-3 max-w-xs min-w-[20rem]',
                styleHeader.container
              )}
            >
              <List type="menu" className="!p-0">
                <ListItem
                  className={clsx(
                    '!p-4 bg-default flex flex-col rounded-t-lg',
                    styleHeader.listItem
                  )}
                >
                  <Text
                    as="div"
                    variant="action"
                    className="text-left overflow-wrap-anywhere"
                  >
                    {session?.user?.name}
                  </Text>
                  <Text
                    as="div"
                    color="subdued"
                    className="overflow-wrap-anywhere"
                  >
                    {session?.user?.loginEmail || session?.user?.email}
                  </Text>
                </ListItem>
                {children ? children : <></>}
                {isMultiplePeriodEnabled ? (
                  <ListItem
                    className={clsx('!px-4 !py-3', styleHeader.listItem)}
                    onClick={() => setIsOpen(true)}
                  >
                    <Text variant="body">Ganti Periode</Text>
                  </ListItem>
                ) : (
                  <></>
                )}
                <ListItem
                  className={clsx('!px-4 !py-3', styleHeader.listItem)}
                  onClick={() => router.push('/logout')}
                >
                  <Text variant="body">Keluar</Text>
                </ListItem>
              </List>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <PeriodDialog
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false)
        }}
      />
    </>
  )
}
